// src/hooks/useManualSave.ts - Manual save functionality for BCP form sections
import { useCallback, useRef, useState, useEffect } from 'react';
import { SaveStatus } from '../types/bcp';
import { FormState } from '../types/bcpForm';
import { updateBCP } from '../services/api';

export interface ManualSaveHook {
  manualSave: (section: string, data: any) => Promise<void>;
  manualSaveStatus: Pick<SaveStatus, 'isManualSaving' | 'lastSaved' | 'errors'>;
}

export const useManualSave = (bcpId: string | undefined, formData: FormState): ManualSaveHook => {
  const [manualSaveStatus, setManualSaveStatus] = useState({
    isManualSaving: {} as { [section: string]: boolean },
    lastSaved: null as Date | null,
    errors: {} as { [section: string]: string | null }
  });

  // Refs for timeout management
  const manualSaveTimeoutRefs = useRef<{ [key: string]: NodeJS.Timeout }>({});

  // Manual save function for specific sections
  const manualSave = useCallback(async (section: string, data: any) => {
    if (!bcpId) return;

    // For sections other than basic business info, validate that Step 1 is complete
    if (section !== 'business_name' && section !== 'business_type' && section !== 'industry' &&
        section !== 'employee_count' && section !== 'annual_revenue' && section !== 'primary_location') {

      // Check if required Step 1 fields are missing
      if (!formData.business_name?.trim() ||
          !formData.business_type?.trim() ||
          !formData.industry?.trim() ||
          !formData.employee_count ||
          formData.employee_count <= 0) {
        console.log('🚫 [MANUAL SAVE] Cannot save section - required Step 1 fields missing');
        throw new Error('Please complete Step 1 (Business Information) before saving other sections');
      }
    }

    try {
      setManualSaveStatus(prev => ({
        ...prev,
        isManualSaving: { ...prev.isManualSaving, [section]: true }
      }));

      // Create a properly typed update payload
      const updatePayload: any = { [section]: data };
      console.log('🔍 [MANUAL SAVE] Sending to backend:', {
        section,
        data,
        updatePayload,
        bcpId
      });

      const result = await updateBCP(parseInt(bcpId), updatePayload);
      console.log('✅ [MANUAL SAVE] Backend response:', result);

      setManualSaveStatus(prev => ({
        ...prev,
        isManualSaving: { ...prev.isManualSaving, [section]: false },
        lastSaved: new Date(),
        errors: { ...prev.errors, [section]: null }
      }));

      // Clear success indicator after 2 seconds
      if (manualSaveTimeoutRefs.current[section]) {
        clearTimeout(manualSaveTimeoutRefs.current[section]);
      }
      manualSaveTimeoutRefs.current[section] = setTimeout(() => {
        setManualSaveStatus(prev => ({
          ...prev,
          isManualSaving: { ...prev.isManualSaving, [section]: false }
        }));
      }, 2000);

    } catch (error) {
      console.error(`Manual save failed for ${section}:`, error);
      setManualSaveStatus(prev => ({
        ...prev,
        isManualSaving: { ...prev.isManualSaving, [section]: false },
        errors: { ...prev.errors, [section]: `Failed to save ${section}` }
      }));
    }
  }, [bcpId, formData]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    const timeoutRefs = manualSaveTimeoutRefs.current;
    return () => {
      Object.values(timeoutRefs).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  return {
    manualSave,
    manualSaveStatus
  };
};
